import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import type {
  BillingInterval,
  Platform,
  Playstyle,
  SubscriptionTier,
  UserType,
} from '@/lib/api/schemas'

/**
 * Onboarding state machine. Mirrors the Android `OnboardingStep` enum and
 * tracks all the form data the user has accumulated so a page refresh
 * resumes exactly where they were — the wizard's whole job is to make
 * abandonment cheap to recover from.
 *
 * Transient data (squadron search results, the currently-selected
 * squadron when applying to JOIN) deliberately lives in component state,
 * NOT here. Persisting it would surface a stale JOIN_SELECT screen for a
 * squadron the user no longer cares about. Android makes the same call.
 */

export type OnboardingStep =
  | 'VERIFY_CHOICE'
  | 'VERIFY_ORG'
  | 'CALLSIGN'
  | 'PATH'
  | 'JOIN_SEARCH'
  | 'JOIN_SELECT'
  | 'JOIN_SENT'
  | 'CREATE_DETAILS'
  | 'CREATE_TIER'
  | 'CREATE_PAYMENT'
  /** First-time-owner tour shown after the payment celebration. Tabbed
   *  walkthrough of management, events, tournaments, integrations.
   *  Skippable — `tourCompletedAt` records dismissal so the wizard
   *  router can leap straight to GAMES on the next mount. Re-openable
   *  later from the in-app Settings → "Show me around". */
  | 'CREATE_TOUR'
  | 'GAMES'
  | 'DONE'

export type OnboardingPath = 'JOIN' | 'CREATE'

export interface CreateDraft {
  name: string
  shortTag: string
  description: string
  unit: string
  playstyle: Playstyle | null
  platform: Platform | null
  /** Verification mode picked on CreateDetailsStep. Maps server-
   *  side to the legacy `requiresVerification` boolean +
   *  `verifiedDomain` snapshot (see stripe lambda
   *  setupSquadronPayment). The legacy `requiresVerification`
   *  field on this draft is kept for migration safety but new
   *  surfaces should read `verificationMode` instead. */
  verificationMode: 'OPEN' | 'HYBRID' | 'VERIFIED_ONLY'
  requiresVerification: boolean
  tier: SubscriptionTier
  billingInterval: BillingInterval
  /** Monthly member fee in pounds — pence conversion happens at API call. */
  memberMonthlyFee: number
}

export const DEFAULT_CREATE_DRAFT: CreateDraft = {
  name: '',
  shortTag: '',
  description: '',
  unit: '',
  playstyle: null,
  platform: null,
  verificationMode: 'OPEN',
  requiresVerification: false,
  tier: 'FREE',
  billingInterval: 'monthly',
  memberMonthlyFee: 0,
}

export interface OnboardingState {
  step: OnboardingStep
  path: OnboardingPath | null
  userType: UserType
  callsign: string
  /** Squadron the user has shortlisted but not yet applied to. */
  selectedSquadronId: string | null
  /** Token from a /join/:token deep link, consumed at INVITE step. */
  pendingInviteToken: string | null
  create: CreateDraft
  /** ID of the squadron that received a join request — used by the
   * JOIN_SENT step to render the right context. */
  joinedSquadronId: string | null
  /** Set once the user lands on /onboarding/done to prevent loop-back. */
  completed: boolean
  /** ISO timestamp the user last dismissed (or completed) the new-owner
   *  tour. Persisted in localStorage so dismissal sticks on this device.
   *  null = never seen → next time CREATE_TOUR is reached, render it.
   *  Cross-device persistence would need a backend field on the squadron
   *  row; deferred until usage data justifies the round-trip. */
  tourCompletedAt: string | null

  // ── Mutators ────────────────────────────────────────────────────────
  setStep: (step: OnboardingStep) => void
  setPath: (path: OnboardingPath) => void
  setUserType: (userType: UserType) => void
  setCallsign: (callsign: string) => void
  setSelectedSquadron: (squadronId: string | null) => void
  setPendingInvite: (token: string | null) => void
  updateCreate: (patch: Partial<CreateDraft>) => void
  resetCreate: () => void
  setJoinedSquadron: (squadronId: string | null) => void
  markCompleted: () => void
  /** Stamp dismissal/completion of the tour. Called by skip + continue. */
  markTourCompleted: () => void
  /** Manual re-open from Settings — clears the dismissal so the tour
   *  surfaces on the next CREATE_TOUR routing pass. */
  resetTour: () => void
  /** Wipe the store back to defaults. Called after successful onboarding. */
  reset: () => void
}

const INITIAL_STATE = {
  step: 'VERIFY_CHOICE' as OnboardingStep,
  path: null as OnboardingPath | null,
  userType: 'STANDARD' as UserType,
  callsign: '',
  selectedSquadronId: null as string | null,
  pendingInviteToken: null as string | null,
  create: DEFAULT_CREATE_DRAFT,
  joinedSquadronId: null as string | null,
  completed: false,
  tourCompletedAt: null as string | null,
}

export const useOnboardingStore = create<OnboardingState>()(
  persist(
    (set) => ({
      ...INITIAL_STATE,

      setStep: (step) => set({ step }),
      setPath: (path) => set({ path }),
      setUserType: (userType) => set({ userType }),
      setCallsign: (callsign) => set({ callsign }),
      setSelectedSquadron: (squadronId) => set({ selectedSquadronId: squadronId }),
      setPendingInvite: (token) => set({ pendingInviteToken: token }),
      updateCreate: (patch) =>
        set((s) => ({ create: { ...s.create, ...patch } })),
      resetCreate: () => set({ create: DEFAULT_CREATE_DRAFT }),
      setJoinedSquadron: (squadronId) => set({ joinedSquadronId: squadronId }),
      markCompleted: () => set({ completed: true }),
      markTourCompleted: () =>
        set({ tourCompletedAt: new Date().toISOString() }),
      resetTour: () => set({ tourCompletedAt: null }),
      reset: () => set({ ...INITIAL_STATE }),
    }),
    {
      name: 'e404-onboarding',
      // v2: removed the never-reachable CREATE_FEES step. Any localStorage
      // that somehow persisted it is remapped to CREATE_PAYMENT so a resume
      // can't land on a step that no longer exists.
      version: 2,
      migrate: (persisted: unknown, version) => {
        const state = persisted as { step?: string } | null
        if (state && version < 2 && state.step === 'CREATE_FEES') {
          return { ...state, step: 'CREATE_PAYMENT' }
        }
        return state as OnboardingState
      },
      // Only persist the fields a resume actually needs. Transient
      // selections (selectedSquadronId) deliberately omitted — see comment
      // at top of file.
      partialize: (s) => ({
        step: s.step,
        path: s.path,
        userType: s.userType,
        callsign: s.callsign,
        pendingInviteToken: s.pendingInviteToken,
        create: s.create,
        joinedSquadronId: s.joinedSquadronId,
        completed: s.completed,
        tourCompletedAt: s.tourCompletedAt,
      }),
    },
  ),
)
